/**
 * @fileoverview Is this name already in use by this author, ASKED SO THAT "NO
 * ANSWER" CANNOT PASS FOR "FREE"?
 *
 * Every page, site and theme is addressable: publishing at a name the author
 * already holds does not add a second one, it REPLACES the first. For a site
 * that also mints a new key, which cuts off every recipient of the old one. So
 * a publish of something NEW must know the name is free, and "free" has to
 * mean every relay we are about to write to said so.
 *
 * THIS REVERSES A DECISION, ON PURPOSE. The slug check this replaces on the
 * publish path was best effort by design: a relay that timed out read as a
 * free name, so that a relay hiccup could never stop an author saving. Ruled
 * otherwise by cloistr-orchestrator on 2026-10-09 from cloistr-common's
 * overwrite sweep: a relay failure is an error, not "free". The cost is small
 * because the relays asked are the relays the publish goes to, so a relay that
 * cannot answer a read was unlikely to take the write either.
 *
 * Taken wins over unanswered. If one relay holds the name, the name is taken,
 * whatever the others did.
 *
 * Reading stays tolerant. `findSlugHolders` keeps its best-effort contract for
 * resolving a readable link, where a slow relay must not make a page vanish.
 */

import type { SlugHolder } from './slugAvailability';

import { probeRelays } from '@/services/nostr/relays';

export type NameCheck =
  | { status: 'free' }
  | { status: 'taken'; holder: SlugHolder }
  /** Nothing was found, but these relays never answered, so nothing is known. */
  | { status: 'unchecked'; relays: string[] };

/**
 * Check one author's name across the given kinds.
 *
 * `allowKind` names a kind whose holder is the thing being REPLACED, which is
 * an edit and allowed: the previous version of the page being edited.
 */
export async function checkName(
  kinds: readonly number[],
  pubkey: string,
  identifier: string,
  relays: string[],
  allowKind?: number
): Promise<NameCheck> {
  if (relays.length === 0) return { status: 'unchecked', relays: [] };

  const answers = await Promise.all(
    kinds.map((kind) =>
      // One kind, one author, one `d`: cloistr-relay answers a complex filter
      // with a bare CLOSED, which is now counted as no answer rather than free.
      probeRelays(relays, { kinds: [kind], authors: [pubkey], '#d': [identifier], limit: 1 })
    )
  );

  for (const [i, kind] of kinds.entries()) {
    if (kind === allowKind) continue;
    // Held to the address asked for: a relay answering with somebody else's
    // event, or another name, must not decide whether this one is taken.
    const held = (answers[i]?.events ?? []).filter(
      (e) =>
        e.kind === kind &&
        e.pubkey === pubkey &&
        e.tags.some((t) => t[0] === 'd' && t[1] === identifier)
    );
    const newest = held.reduce<(typeof held)[number] | null>(
      (best, e) => (!best || e.created_at > best.created_at ? e : best),
      null
    );
    if (newest) {
      return { status: 'taken', holder: { kind, pubkey, identifier, createdAt: newest.created_at } };
    }
  }

  const unanswered = [...new Set(answers.flatMap((a) => a.unanswered))];
  return unanswered.length > 0 ? { status: 'unchecked', relays: unanswered } : { status: 'free' };
}
